import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dove, comment } from '../../assets/images';
import api from '../../../services/api';

const AVATAR_COLORS = [
  'bg-rose-400', 'bg-teal-500', 'bg-amber-500',
  'bg-indigo-400', 'bg-pink-400', 'bg-sky-400',
  'bg-green-500', 'bg-violet-400', 'bg-cyan-400',
];
const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");
const mediaSrc = (p) =>
  p?.startsWith("/uploads") ? `${API_ROOT}${p}` : p;

function getAvatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
}

function AvatarSmall({ name, avatar }) {
  console.log({name, avatar });
  return (
    <div
      className={`w-8 h-8 sm:w-[34px] sm:h-[34px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center`}
      
    >
        <img
          src={avatar.startsWith("/uploads") ? `${API_ROOT}${avatar}` : avatar}
          alt={name}
          className="w-full h-full object-cover rounded-full"
        />
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function Avatar({ name, avatar }) {
  if (avatar) {
    return (
      <img
        src={avatar.startsWith("/uploads") ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${avatar}` : avatar}
        alt={name}
        className="w-[44px] h-[44px] sm:w-[51.74px] sm:h-[51.74px] rounded-full object-cover border border-gray-200 flex-shrink-0"
      />
    );
  }

  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const color = getAvatarColor(name);

  return (
    <div className={`w-[44px] h-[44px] sm:w-[51.74px] sm:h-[51.74px] rounded-full ${color} flex items-center justify-center text-white text-[18px] sm:text-[22px] font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

function CommentItem({ comment }) {
  return (
    <div className="flex gap-3 mb-6 pl-4 border-l-2 border-gray-100">
      <AvatarSmall name={comment.author?.name || 'User'} avatar={comment.author?.avatar} />
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-sm">{comment.author?.name}</span>
          <span className="text-xs text-gray-400">{timeAgo(comment.createdAt)}</span>
        </div>
        <p className="text-sm text-gray-700 mt-1 leading-relaxed">{comment.content}</p>
      </div>
    </div>
  );
}

function PostComments({ postId }) {
  const { data: commentsData, isLoading } = useQuery({
    queryKey: ['post-comments', postId],
    queryFn: () => api.getComments(postId),
    select: (res) => res?.comments || [],
  });

  if (isLoading) return <p className="text-center py-6 text-gray-400 text-sm">Loading comments...</p>;
  if (!commentsData || commentsData.length === 0) return <p className="text-center py-6 text-gray-400 text-sm">No comments yet.</p>;

  return (
    <div className="space-y-6">
      {commentsData.map(comment => (
        <CommentItem key={comment.id} comment={comment} />
      ))}
    </div>
  );
}

export default function Community() {
  const [followed, setFollowed] = useState({});
  const [expandedPosts, setExpandedPosts] = useState({});
  const [page, setPage] = useState(1);
  const [allPosts, setAllPosts] = useState([]); // Accumulate posts for "Load More"
const isImage = (p) => /\.(jpe?g|png|gif|jfif|webp|avif|svg)$/i.test(p);

  const { data: newPosts = [], isLoading, isFetching } = useQuery({
    queryKey: ['community-posts', page],
    queryFn: () => api.getPosts(page, 10),
    select: (res) => {
      const list = res?.posts || res?.data || [];
      return list.map(p => ({
        id: p.id,
        name: p.author?.name || 'Unknown',
        avatar: p.author?.avatar,
        time: timeAgo(p.createdAt),
        text: p.content || '',
        attachment: p.attachment ? 
          (p.attachment.startsWith('http') ? p.attachment : `${process.env.REACT_APP_API_URL?.replace('/api', '')}${p.attachment}`) 
          : null,
        bless: p.engagement?.blessCount || 0,
        commentsCount: p.engagement?.commentCount || 0,
      }));
    },
    keepPreviousData: true,
  });

  // Append new posts when page changes
  React.useEffect(() => {
    if (newPosts.length > 0) {
      setAllPosts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const filteredNew = newPosts.filter(p => !existingIds.has(p.id));
        return [...prev, ...filteredNew];
      });
    }
  }, [newPosts]);

  const toggleFollow = (id) => {
    setFollowed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleComments = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  const hasMore = newPosts.length > 0; // Simple check - improve with pagination metadata if available

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }} className="w-full">
      <h1 className="text-[22px] sm:text-[28px] md:text-[33px] lg:text-[30px] 2xl:text-[30px] font-black text-gray-900 tracking-wide capitalize mb-3">
        Faith Feed
      </h1>

      {isLoading && allPosts.length === 0 ? (
        <div className="text-center py-12 text-gray-400">Loading Faith Feed...</div>
      ) : (
        <div className="space-y-5">
          {allPosts.map(post => {
            const isExpanded = expandedPosts[post.id];

            return (
              <div key={post.id} className="bg-white rounded-[20px] sm:rounded-[24px] border border-gray-100 shadow-sm p-4 sm:p-5 lg:px-6 lg:py-5">
                <div className="flex items-start gap-3 mb-4">
                  <Avatar name={post.name} avatar={post.avatar} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[16px] sm:text-[18px] font-semibold text-gray-900">{post.name}</span>
                      {/* <span className="text-gray-300 text-xs">•</span>
                      <button 
                        onClick={() => toggleFollow(post.id)} 
                        className={`text-sm font-semibold ${followed[post.id] ? 'text-gray-400' : 'text-[#1F3A60]'}`}
                      >
                        {followed[post.id] ? 'Following' : 'Follow'}
                      </button> */}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{post.time}</p>
                  </div>
                </div>

                {post.text && <p className="text-[15px] sm:text-[16px] text-gray-700 leading-[1.8] mb-4">{post.text}</p>}

                {post.image && (
                  <div className="rounded-2xl overflow-hidden mb-5">
                    <img src={post.image} alt="post" className="w-full max-h-[420px] object-cover" />
                  </div>
                )}
  {post.attachment && (
        <div className="relative">
          {isImage(post.attachment) ? (
            <img
              src={mediaSrc(post.attachment)}
              alt=""
              className="w-full max-h-[280px] sm:max-h-[360px] md:max-h-[420px] object-cover"
            />
          ) : (
            <video
              src={mediaSrc(post.attachment)}
              controls
              className="w-full max-h-[280px] sm:max-h-[360px] md:max-h-[420px] object-cover"
            />
          )}
        </div>
      )}
                <div className="flex items-center gap-6 mt-4">
                  <button className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700">
                    <img src={Dove} alt="bless" className="w-7 h-7" />
                    {post.bless} Bless
                  </button>

                  <button
                    onClick={() => toggleComments(post.id)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                  >
                    <img src={comment} alt="comment" className="w-7 h-7" />
                    {post.commentsCount} Comments
                  </button>
                </div>

                {isExpanded && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-500 mb-4">Comments</p>
                    <PostComments postId={post.id} />
                  </div>
                )}
              </div>
            );
          })}

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={loadMore}
                disabled={isLoading}
                className="px-8 py-3 bg-[#1F3A60] text-white rounded-xl font-medium hover:bg-[#172e4e] disabled:opacity-60"
              >
                {isLoading ? 'Loading...' : 'Load More Posts'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
